/**
 * pdp-coupon-countdown.js
 *
 * 折扣码 / 会员价倒计时驱动。
 *
 * 两套倒计时各自内嵌在对应的 UI 容器内：
 *   - [data-mp-ui="member"] 内：会员价倒计时（per-variant countdownEndTime）
 *   - [data-mp-ui="coupon"] 内：折扣码倒计时（全局 cd_config.end_time）
 *
 * variant 切换时，member-price.js 先切换两套容器的 hidden 状态，
 * 随后 pdp-coupon-countdown.js 的 variant:changed 监听器
 * 只初始化并驱动**当前可见容器**内的倒计时 DOM，隐藏容器内的倒计时静默停止。
 *
 * HTML 约定（两套容器内结构相同）：
 *   <div class="pdp-coupon-countdown"
 *     data-countdown-end="{Unix ms}"
 *     data-label="{文案}"
 *     hidden
 *   >
 *     <span class="pdp-coupon-countdown__label"></span>
 *     <span class="pdp-coupon-countdown__ends-in">Ends In</span>
 *     <span class="pdp-coupon-countdown__timer">
 *       <span data-cd-hh>00</span>:<span data-cd-mm>00</span>:<span data-cd-ss>00</span>
 *     </span>
 *   </div>
 *
 * 会员价倒计时容器内还有 data-countdown-variant-map（per-variant endMs 映射），
 * variant 切换时读取并更新 data-countdown-end。
 *
 * 显隐逻辑：
 *   - end_time 未配置 / 已过期 → 隐藏
 *   - remainMs > 48h（47:59:59）→ 不展示，轮询进入窗口后自动显示
 *   - 0 < remainMs ≤ 47:59:59 → 展示并倒计时
 */

(function () {
  'use strict';

  var INIT_KEY = '__pdpCouponCountdownInit__';
  if (window[INIT_KEY]) return;
  window[INIT_KEY] = true;

  var MAX_REMAIN_MS = 172799000;   // 47:59:59（最大 48 小时）
  var POLL_INTERVAL_MS = 60 * 1000;

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function msToHMS(ms) {
    var totalSec = Math.max(0, Math.floor(ms / 1000));
    var hh = Math.floor(totalSec / 3600);
    var mm = Math.floor((totalSec % 3600) / 60);
    var ss = totalSec % 60;
    return { hh: pad2(hh), mm: pad2(mm), ss: pad2(ss) };
  }

  /**
   * 返回当前可见 UI 容器（[data-mp-ui]:not([hidden])）内的所有 .pdp-coupon-countdown 元素。
   * 若无 [data-mp-ui] 结构，回退到全局查找（兼容旧结构）。
   */
  function getActiveCountdownEls() {
    var activeUi = document.querySelector('[data-mp-ui]:not([hidden])');
    var scope = activeUi || document;
    return Array.prototype.slice.call(
      scope.querySelectorAll('.pdp-coupon-countdown')
    );
  }

  function CountdownInstance(el) {
    this.el         = el;
    var endAttr     = el.getAttribute('data-countdown-end');
    this.endMs      = endAttr ? parseInt(endAttr, 10) : 0;
    this.label      = el.getAttribute('data-label') || '';
    this.hhEl       = el.querySelector('[data-cd-hh]');
    this.mmEl       = el.querySelector('[data-cd-mm]');
    this.ssEl       = el.querySelector('[data-cd-ss]');
    this.labelEl    = el.querySelector('.pdp-coupon-countdown__label');
    this._timer     = null;
    this._pollTimer = null;
    this._couponActive = false;
  }

  CountdownInstance.prototype.getRemainMs = function () {
    return this.endMs > 0 ? this.endMs - Date.now() : 0;
  };

  CountdownInstance.prototype.show = function () {
    this.el.hidden = false;
    this.el.removeAttribute('hidden');
    var parent = this.el.closest('.pdp-product__block--coupon');
    if (parent) parent.classList.add('has-countdown');
  };

  CountdownInstance.prototype.hide = function () {
    this.el.hidden = true;
    var parent = this.el.closest('.pdp-product__block--coupon');
    if (parent) parent.classList.remove('has-countdown');
  };

  CountdownInstance.prototype.setLabel = function () {
    if (this.labelEl) this.labelEl.textContent = this.label;
  };

  CountdownInstance.prototype.updateDisplay = function (remainMs) {
    var hms = msToHMS(remainMs);
    if (this.hhEl) this.hhEl.textContent = hms.hh;
    if (this.mmEl) this.mmEl.textContent = hms.mm;
    if (this.ssEl) this.ssEl.textContent = hms.ss;
  };

  CountdownInstance.prototype.stopTimer = function () {
    if (this._timer)     { clearInterval(this._timer);     this._timer = null; }
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
  };

  CountdownInstance.prototype.startTick = function () {
    var self = this;
    self.stopTimer();
    self._timer = setInterval(function () {
      var remain = self.getRemainMs();
      if (remain <= 0) { self.stopTimer(); self.hide(); return; }
      self.updateDisplay(remain);
    }, 1000);
  };

  CountdownInstance.prototype.evaluate = function () {
    // 直降倒计时活跃时抑制折扣码倒计时（pdp-saving-countdown.js 设置全局标记）
    if (window.__pdpSavingCountdownActive) { this.stopTimer(); this.hide(); return; }

    if (!this._couponActive) { this.stopTimer(); this.hide(); return; }

    var remain = this.getRemainMs();
    if (remain <= 0) { this.stopTimer(); this.hide(); return; }

    if (remain > MAX_REMAIN_MS) {
      this.stopTimer();
      this.hide();
      var self = this;
      self._pollTimer = setInterval(function () {
        if (self.getRemainMs() <= MAX_REMAIN_MS) {
          clearInterval(self._pollTimer);
          self._pollTimer = null;
          self.evaluate();
        }
      }, POLL_INTERVAL_MS);
      return;
    }

    this.setLabel();
    this.updateDisplay(remain);
    this.show();
    this.startTick();
  };

  CountdownInstance.prototype.setCouponActive = function (active) {
    if (this._couponActive === active) return;
    this._couponActive = active;
    // 直降倒计时活跃时抑制：不因 couponActive 变为 true 而显示
    if (active && window.__pdpSavingCountdownActive) {
      this.stopTimer();
      this.hide();
      return;
    }
    this.evaluate();
  };

  CountdownInstance.prototype.destroy = function () {
    this.stopTimer();
  };

  var instances = [];

  /**
   * 销毁旧实例，重新初始化当前可见容器内的倒计时 DOM。
   */
  function initAll() {
    instances.forEach(function (inst) { inst.hide(); inst.destroy(); });
    instances = [];
    getActiveCountdownEls().forEach(function (el) {
      instances.push(new CountdownInstance(el));
    });
  }

  function setAllCouponActive(active) {
    instances.forEach(function (inst) { inst.setCouponActive(active); });
  }

  /**
   * 从当前可见容器内读取 data-countdown-variant-map，
   * 按 variantId 返回对应 endMs（0 表示无配置）。
   */
  function getEndMsForVariant(variantId) {
    var activeUi = document.querySelector('[data-mp-ui]:not([hidden])');
    var mapEl = null;
    if (activeUi) {
      mapEl = activeUi.querySelector('[data-countdown-variant-map]');
    }
    // 无 mp-ui 结构时（旧版 pdp-coupon-code.liquid），从 coupon 容器读取
    if (!mapEl) {
      var couponBlock = document.querySelector('.pdp-product__block--coupon');
      if (couponBlock) {
        mapEl = couponBlock.querySelector('[data-countdown-variant-map]');
      }
    }
    if (!mapEl) return 0;
    try {
      var map = JSON.parse(mapEl.textContent);
      var ms = map.variants && map.variants[String(variantId)];
      return ms ? parseInt(ms, 10) : 0;
    } catch (e) {
      return 0;
    }
  }

  /**
   * 判断当前 variant 是否有折扣可展示（驱动 couponActive）。
   * 读取 data-pdp-coupon-data JSON（折扣码 UI 专用）：
   *   - basicDiscountCode 非空 → 全球/国家折扣，所有变体均有
   *   - variantDiscounts 含当前 variantId → 该变体有专属折扣
   * 会员价 UI 可见时无需此判断（member-price.js 负责），直接视为 active。
   */
  function checkCouponActiveForVariant(variantId) {
    // 会员价 UI 可见：倒计时直接 active（由 endMs 是否有效决定显隐）
    var memberUi = document.querySelector('[data-mp-ui="member"]');
    if (memberUi && !memberUi.hidden) {
      setAllCouponActive(true);
      return;
    }

    // 折扣码 UI 可见：读取折扣码数据判断
    var dataEl = document.querySelector('[data-pdp-coupon-data]');
    if (!dataEl) { setAllCouponActive(false); return; }

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { setAllCouponActive(false); return; }

    if (data.basicDiscountCode) { setAllCouponActive(true); return; }

    if (variantId && Array.isArray(data.variantDiscounts)) {
      var hasVariant = data.variantDiscounts.some(function (vd) {
        return String(vd.variantId) === String(variantId);
      });
      setAllCouponActive(hasVariant);
      return;
    }

    setAllCouponActive(false);
  }

  /**
   * 直降倒计时状态变化：活跃时隐藏折扣码倒计时，结束时重新 evaluate
   */
  document.addEventListener('pdp:saving-countdown-status', function (e) {
    var active = e.detail && e.detail.active;
    instances.forEach(function (inst) {
      if (active) {
        inst.stopTimer();
        inst.hide();
      } else {
        inst.evaluate();
      }
    });
  });

  /**
   * mp:ui-switched：member-price.js 在 _switchDiscountUI 完成后派发，
   * 此时 [data-mp-ui] 的 hidden 状态已就绪，可安全读取当前可见容器。
   * 替代 variant:changed，避免两者竞争导致 getActiveCountdownEls() 读到旧状态。
   */
  document.addEventListener('mp:ui-switched', function (evt) {
    var variantId = evt.detail && evt.detail.variantId;

    // 先清理旧实例（hide 清除 has-countdown class，destroy 停计时器）
    instances.forEach(function (inst) { inst.hide(); inst.destroy(); });
    instances = [];

    // 重新初始化当前可见容器的倒计时 DOM
    getActiveCountdownEls().forEach(function (el) {
      instances.push(new CountdownInstance(el));
    });

    // 会员价容器可见时，从 variant-map 更新 per-variant endMs
    var memberUi = document.querySelector('[data-mp-ui="member"]');
    if (memberUi && !memberUi.hidden && variantId) {
      var newEndMs = getEndMsForVariant(variantId);
      instances.forEach(function (inst) {
        inst.endMs = newEndMs;
        inst.el.setAttribute('data-countdown-end', String(newEndMs));
      });
    }

    // 折扣码容器可见时，若有 variant-map 则更新 per-variant endMs
    // （仅 pdp-coupon-code.liquid 旧版添加了此 map，v2 的 cd_config.end_time 全局不变）
    var couponUi = document.querySelector('[data-mp-ui="coupon"]');
    if (couponUi && !couponUi.hidden && variantId) {
      var mapEl2 = couponUi.querySelector('[data-countdown-variant-map]');
      if (mapEl2) {
        var newEndMs2 = getEndMsForVariant(variantId);
        instances.forEach(function (inst) {
          inst.endMs = newEndMs2;
          inst.el.setAttribute('data-countdown-end', String(newEndMs2));
        });
      }
    }

    checkCouponActiveForVariant(variantId);
  });

  /**
   * variant:changed 仅作兜底：处理没有会员价模块（无 _switchDiscountUI 派发）的情况，
   * 即纯折扣码商品切换 variant 时倒计时的 couponActive 判断。
   * 有 mp:ui-switched 时此处不重复执行（折扣码全局 endMs 不变，无需 reinit）。
   */
  document.addEventListener('variant:changed', function (evt) {
    // 若页面有 [data-mp-ui] 结构，倒计时由 mp:ui-switched 驱动，此处跳过
    if (document.querySelector('[data-mp-ui]')) return;
    var v = evt.currentVariant || (evt.detail && evt.detail.variant);
    var variantId = v ? v.id : null;

    // 无 mp-ui 结构时，从 pdp-coupon-code 容器内的 variant-map 更新 per-variant endMs
    if (variantId) {
      var newEndMs = getEndMsForVariant(variantId);
      instances.forEach(function (inst) {
        inst.endMs = newEndMs;
        inst.el.setAttribute('data-countdown-end', String(newEndMs));
      });
      instances.forEach(function (inst) { inst.evaluate(); });
    }

    checkCouponActiveForVariant(variantId);
  });

  function boot() {
    initAll();
    // 初始 variantId：优先从折扣码数据读，回退 URL param
    var initVariantId = null;
    var dataEl = document.querySelector('[data-pdp-coupon-data]');
    if (dataEl) {
      try {
        var d = JSON.parse(dataEl.textContent);
        initVariantId = d.currentVariantId || null;
      } catch (e) {}
    }
    if (!initVariantId) {
      initVariantId = new URLSearchParams(window.location.search).get('variant');
    }
    checkCouponActiveForVariant(initVariantId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
